/**
 * Documents (design board 2; MongoDB): one collection's documents, found with a filter and a sort as in the Mongo shell;
 * the one picked opens as JSON, checked as you type with what you changed, then saved (replaced), duplicated or deleted.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FileArrowUp, Plus, Sparkle } from '@phosphor-icons/react';
import { ConfirmDialog, EmptyState, ErrorState, Loading, ReadOnlyBadge, errorText } from './DataCommon';
import DocTree from './DocTree';
import JsonImportDialog from './JsonImportDialog';
import { deleteDoc, insertDoc, listDocs, replaceDoc } from '../../services/DataService';
import { docDiff, docIdLabel, formatCount, parseLooseJson } from '../../utils/dataModel';

const PAGE = 20;

function Preview({ doc }) {
  const entries = Object.entries(doc || {}).slice(0, 5);
  return (
    <pre className="data-doc-pre">
      {entries.map(([key, value], index) => {
        let shown;
        let cls = 'is-v';
        if (key === '_id') { shown = docIdLabel(value); } else if (typeof value === 'string') { shown = JSON.stringify(value.length > 60 ? `${value.slice(0, 57)}…` : value); cls = 'is-s'; } else if (typeof value === 'number' || typeof value === 'boolean') { shown = String(value); cls = 'is-n'; } else if (Array.isArray(value)) { shown = `[ ${value.length} item${value.length === 1 ? '' : 's'} ]`; } else if (value && typeof value === 'object') { shown = value.$date ? `Date(${value.$date})` : '{ … }'; } else { shown = 'null'; }
        return (
          <React.Fragment key={key}>
            <span className="is-k">{key}</span>: <span className={cls}>{shown}</span>{index < entries.length - 1 ? '\n' : ''}
          </React.Fragment>
        );
      })}
    </pre>
  );
}

Preview.propTypes = { doc: PropTypes.object };

const pretty = (doc) => JSON.stringify(doc, null, 2);

export default function DocumentsView({ projectId, store, collection, readOnly = false, initialFilter = null, onApplied, onOpenQuery }) {
  const [filterText, setFilterText] = useState(initialFilter || '');
  const [sortText, setSortText] = useState('');
  const [applied, setApplied] = useState({ filter: initialFilter || undefined, sort: undefined });
  const [mode, setMode] = useState('json'); // the editor: 'json' | 'tree'
  const [importing, setImporting] = useState(false);
  const [inputError, setInputError] = useState(null);
  const [skip, setSkip] = useState(0);
  const [answer, setAnswer] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // { doc } or { doc: null, isNew: true }
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [notice, setNotice] = useState(null);
  const canWrite = !readOnly && !store.readOnly;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listDocs(projectId, collection, { filter: applied.filter, sort: applied.sort, limit: PAGE, skip });
      setAnswer(result);
      return result;
    } catch (e) {
      setError(errorText(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, [projectId, collection, applied, skip]);

  useEffect(() => { load(); }, [load]);

  const open = (doc, isNew = false) => {
    setSelected({ doc, isNew });
    setText(isNew ? (doc ? pretty(doc) : '{\n  \n}') : pretty(doc));
    setSaveError(null);
    setNotice(null);
  };

  const find = (event) => {
    event?.preventDefault();
    const filter = parseLooseJson(filterText);
    const sort = parseLooseJson(sortText);
    if (!filter.ok) { setInputError(`The filter is not valid JSON: ${filter.error}`); return; }
    if (!sort.ok) { setInputError(`The sort is not valid JSON: ${sort.error}`); return; }
    setInputError(null);
    setSkip(0);
    setApplied({ filter: filter.value !== undefined ? JSON.stringify(filter.value) : undefined, sort: sort.value !== undefined ? JSON.stringify(sort.value) : undefined });
  };

  const parsed = useMemo(() => {
    if (!selected) { return null; }
    try {
      const value = JSON.parse(text);
      if (!value || typeof value !== 'object' || Array.isArray(value)) { return { ok: false, error: 'A document is a JSON object: { … }' }; }
      return { ok: true, value };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  }, [text, selected]);
  const diff = useMemo(() => (parsed?.ok && selected?.doc && !selected.isNew ? docDiff(selected.doc, parsed.value) : []), [parsed, selected]);
  const idChanged = parsed?.ok && selected?.doc && !selected.isNew && JSON.stringify(parsed.value._id) !== JSON.stringify(selected.doc._id);
  const dirty = selected?.isNew ? true : diff.length > 0;

  const save = async () => {
    if (!parsed?.ok || idChanged) { return; }
    setSaving(true);
    setSaveError(null);
    try {
      if (selected.isNew) {
        const result = await insertDoc(projectId, collection, parsed.value);
        const fresh = await load();
        const created = fresh?.docs?.find((d) => JSON.stringify(d._id) === JSON.stringify(result?._id));
        if (created) { open(created); } else { setSelected(null); }
        setNotice('Document added');
      } else {
        const result = await replaceDoc(projectId, collection, selected.doc._id, parsed.value);
        setSelected({ doc: parsed.value, isNew: false });
        setText(pretty(parsed.value));
        setNotice('Saved');
        load();
        onApplied?.({ ...result, summary: `Saved a document in ${collection}` });
      }
    } catch (e) {
      setSaveError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const duplicate = () => {
    if (!parsed?.ok) { return; }
    const { _id, ...rest } = parsed.value; // eslint-disable-line no-unused-vars
    open(rest, true);
  };

  const remove = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const result = await deleteDoc(projectId, collection, selected.doc._id);
      setConfirmDelete(false);
      setSelected(null);
      load();
      onApplied?.({ ...result, summary: `Deleted a document from ${collection}` });
    } catch (e) {
      setSaveError(errorText(e));
      setConfirmDelete(false);
    } finally {
      setSaving(false);
    }
  };

  const docs = answer?.docs || [];
  const total = Number(answer?.total ?? docs.length);
  const status = !parsed ? null : !parsed.ok
    ? { ok: false, text: `Not valid JSON: ${parsed.error}` }
    : idChanged
      ? { ok: false, text: 'The _id cannot change: duplicate the document instead' }
      : { ok: true, text: selected.isNew ? 'Valid JSON · a new document' : `Valid JSON · ${diff.length ? `changes: ${diff.join(', ')}` : 'no changes'}` };

  return (
    <div className="data-view" data-testid="documents-view">
      <div className="data-view-head">
        <h1>{collection}</h1>
        {answer && <span className="data-pill">{formatCount(total)} document{total === 1 ? '' : 's'}</span>}
        {!canWrite && <ReadOnlyBadge reason={readOnly ? 'You are a viewer of this project' : 'This store is read-only here'} />}
        <span className="data-grow" />
        <button
          className="data-btn"
          onClick={() => onOpenQuery?.(JSON.stringify({ collection, find: parseLooseJson(filterText).value || {}, limit: 50 }, null, 2))}
          type="button"
        >
          Query
        </button>
        {canWrite && <button className="data-btn" data-testid="import-json" onClick={() => setImporting(true)} type="button"><FileArrowUp aria-hidden size={15} />Import JSON</button>}
        {canWrite && <button className="data-btn is-primary" onClick={() => open(null, true)} type="button"><Plus aria-hidden size={15} />Add document</button>}
      </div>

      <form className="data-card data-toolbar" onSubmit={find}>
        <label className="data-label" htmlFor="data-mfilter">Filter</label>
        <input
          className="data-input data-mono is-wide"
          id="data-mfilter"
          onChange={(e) => setFilterText(e.target.value)}
          placeholder='{ rating: { $lte: 2 }, status: "published" }'
          value={filterText}
        />
        <label className="data-label" htmlFor="data-msort">Sort</label>
        <input className="data-input data-mono" id="data-msort" onChange={(e) => setSortText(e.target.value)} placeholder="{ createdAt: -1 }" value={sortText} />
        <button className="data-btn" type="submit">Find</button>
        <button className="data-btn is-ai" onClick={() => onOpenQuery?.(null, { ask: true })} type="button"><Sparkle aria-hidden size={15} />Describe it instead…</button>
        {inputError && <p className="data-error-text data-full" role="alert">{inputError}</p>}
      </form>

      {error && <ErrorState error={error} onRetry={load} />}
      {!error && !answer && loading && <Loading label={`Loading ${collection}…`} />}
      {answer && (
        <div className="data-split">
          <div className="data-card data-doc-list">
            <div className="data-card-head data-muted">
              {formatCount(total)} {applied.filter ? 'match' : 'in all'}{docs.length ? ` · showing ${formatCount(skip + 1)}–${formatCount(skip + docs.length)}` : ''}
            </div>
            {!docs.length && <EmptyState title={applied.filter ? 'No document matches' : 'No documents yet'} />}
            <ul aria-label={`Documents in ${collection}`} className="data-plain-list">
              {docs.map((doc, index) => {
                const on = selected && !selected.isNew && JSON.stringify(selected.doc?._id) === JSON.stringify(doc._id);
                return (
                  <li key={JSON.stringify(doc._id) || index}>
                    <button aria-current={on ? 'true' : undefined} className={`data-doc${on ? ' is-on' : ''}`} data-testid="doc-item" onClick={() => open(doc)} type="button">
                      <Preview doc={doc} />
                    </button>
                  </li>
                );
              })}
            </ul>
            {total > PAGE && (
              <div className="data-table-foot">
                <span />
                <span className="data-row-gap">
                  <button className="data-btn" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} type="button">Previous</button>
                  <button className="data-btn" disabled={skip + docs.length >= total} onClick={() => setSkip(skip + PAGE)} type="button">Next</button>
                </span>
              </div>
            )}
          </div>

          <section aria-labelledby="data-doc-title" className="data-card data-doc-editor">
            {!selected ? (
              <EmptyState title="Pick a document">Its JSON opens here{canWrite ? ', to change and save' : ''}.</EmptyState>
            ) : (
              <>
                <div className="data-card-head">
                  <h2 className="data-mono" id="data-doc-title">{selected.isNew ? 'New document' : docIdLabel(selected.doc._id)}</h2>
                  {!selected.isNew && dirty && <span className="data-pill is-warn">Edited</span>}
                  {notice && !dirty && <span className="data-pill is-ok" role="status">{notice}</span>}
                  <span className="data-grow" />
                  <div aria-label="Document view" className="data-seg" role="group">
                    <button aria-pressed={mode === 'tree'} disabled={!parsed?.ok && mode !== 'tree'} onClick={() => setMode('tree')} type="button">Tree</button>
                    <button aria-pressed={mode === 'json'} onClick={() => setMode('json')} type="button">JSON</button>
                  </div>
                </div>
                {mode === 'tree' && parsed?.ok ? (
                  <DocTree
                    doc={parsed.value}
                    editable={canWrite}
                    onChange={(next) => { setText(pretty(next)); setNotice(null); }}
                  />
                ) : (
                  <>
                    <label className="data-sr" htmlFor="data-doc-json">Document JSON</label>
                    <textarea
                      className="data-textarea data-mono"
                      id="data-doc-json"
                      onChange={(e) => { setText(e.target.value); setNotice(null); }}
                      readOnly={!canWrite}
                      rows={16}
                      spellCheck={false}
                      value={text}
                    />
                  </>
                )}
                {status && (
                  <p className={`data-validation ${status.ok ? 'is-ok' : 'is-bad'}`} data-testid="doc-validation">{status.text}</p>
                )}
                {saveError && <p className="data-error-text" role="alert">{saveError}</p>}
                {canWrite && (
                  <div className="data-actions">
                    <button className="data-btn is-primary" disabled={saving || !parsed?.ok || idChanged || !dirty} onClick={save} type="button">
                      {saving ? 'Saving…' : 'Save document'}
                    </button>
                    {!selected.isNew && <button className="data-btn" disabled={!parsed?.ok} onClick={duplicate} type="button">Duplicate</button>}
                    {selected.isNew && <button className="data-btn" onClick={() => setSelected(null)} type="button">Cancel</button>}
                    <span className="data-grow" />
                    {!selected.isNew && <button className="data-btn is-danger" onClick={() => setConfirmDelete(true)} type="button">Delete…</button>}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {importing && (
        <JsonImportDialog
          collection={collection}
          onClose={() => setImporting(false)}
          onImported={(answer, name) => {
            load();
            onApplied?.({ ...answer, summary: `Imported ${formatCount(answer.inserted)} document${answer.inserted === 1 ? '' : 's'} from ${name} into ${collection}` });
          }}
          projectId={projectId}
        />
      )}

      {confirmDelete && selected?.doc && (
        <ConfirmDialog busy={saving} onClose={() => setConfirmDelete(false)} onConfirm={remove} title="Delete this document?">
          <p><span className="data-mono">{docIdLabel(selected.doc._id)}</span> goes from <strong>{collection}</strong>. You can Undo for 10 minutes.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

DocumentsView.propTypes = {
  collection: PropTypes.string.isRequired,
  initialFilter: PropTypes.string,
  onApplied: PropTypes.func,
  onOpenQuery: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  store: PropTypes.object.isRequired,
};
